import type { TextContentData, TypographyStyle } from '@/components/canvas/document/publicTypes';
import { isTextBlockEmpty, textBlockToPlainText, textDocumentToPlainText } from './textPlain';
import {
  createTextId,
  type InlineTextStyle,
  type TextBlock,
  type TextBlockType,
  type TextDocument,
  type TextRun,
} from './textDocument';

function stableStyle(style?: InlineTextStyle): string {
  if (!style) return '';
  return JSON.stringify(Object.keys(style).sort().reduce<Record<string, unknown>>((acc, key) => {
    acc[key] = (style as Record<string, unknown>)[key];
    return acc;
  }, {}));
}

function normalizeCssColor(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const raw = value.trim().toLowerCase();
  const hex = raw.match(/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
  if (hex) {
    const body = hex[1]!;
    if (body.length === 3) {
      return `#${body[0]}${body[0]}${body[1]}${body[1]}${body[2]}${body[2]}`;
    }
    return `#${body.slice(0, 6)}`;
  }
  const rgb = raw.match(/^rgba?\(\s*([.\d]+)\s*,\s*([.\d]+)\s*,\s*([.\d]+)(?:\s*,\s*([.\d]+))?\s*\)$/);
  if (rgb) {
    const alpha = rgb[4] == null ? 1 : Number(rgb[4]);
    if (Number.isFinite(alpha) && alpha !== 1) return raw;
    const toHex = (part: string) => {
      const n = Math.max(0, Math.min(255, Math.round(Number(part))));
      return n.toString(16).padStart(2, '0');
    };
    return `#${toHex(rgb[1]!)}${toHex(rgb[2]!)}${toHex(rgb[3]!)}`;
  }
  return raw;
}

function cssColorsEquivalent(a: string | undefined, b: string | undefined): boolean {
  const ca = normalizeCssColor(a);
  const cb = normalizeCssColor(b);
  return !!ca && !!cb && ca === cb;
}

function fontWeightsEquivalent(a: number | string | undefined, b: number | string | undefined): boolean {
  if (a == null || b == null) return false;
  const an = Number(a);
  const bn = Number(b);
  if (Number.isFinite(an) && Number.isFinite(bn)) return an === bn;
  return String(a) === String(b);
}

function stripShellEquivalentStyle(
  style: InlineTextStyle | undefined,
  shell: TypographyStyle | undefined,
): InlineTextStyle | undefined {
  if (!style) return undefined;
  const next: InlineTextStyle = { ...style };

  // Remove falsy boolean properties (false / undefined both mean "not active").
  // This lets callers pass `{ bold: false }` to remove bold cleanly.
  if (!next.bold) delete next.bold;
  if (!next.italic) delete next.italic;
  if (!next.underline) delete next.underline;
  if (!next.strike) delete next.strike;
  if (!next.superscript) delete next.superscript;
  if (!next.subscript) delete next.subscript;

  // Remove explicitly-undefined value properties so they don't pollute stored data.
  if (next.color === undefined) delete next.color;
  if (next.backgroundColor === undefined) delete next.backgroundColor;
  if (next.fontSize === undefined) delete next.fontSize;
  if (next.fontFamily === undefined) delete next.fontFamily;
  if (next.fontWeight === undefined) delete next.fontWeight;
  if (next.fontStyle === undefined) delete next.fontStyle;
  if (next.letterSpacing === undefined) delete next.letterSpacing;
  if (next.lineHeight === undefined) delete next.lineHeight;

  if (shell) {
    if (next.fontFamily === shell.fontFamily) delete next.fontFamily;
    if (next.fontSize === shell.fontSize) delete next.fontSize;
    if (fontWeightsEquivalent(next.fontWeight, shell.fontWeight)) delete next.fontWeight;
    if (next.fontStyle === shell.fontStyle) delete next.fontStyle;
    if (cssColorsEquivalent(next.color, shell.color)) delete next.color;
    if (next.lineHeight === shell.lineHeight) delete next.lineHeight;
    if (next.letterSpacing === shell.letterSpacing) delete next.letterSpacing;
  }
  // superscript and subscript are mutually exclusive
  if (next.superscript) delete next.subscript;
  if (next.subscript) delete next.superscript;
  return Object.keys(next).length ? next : undefined;
}

function normalizeRuns(runs: TextRun[], shell?: TypographyStyle): TextRun[] {
  const normalized: TextRun[] = [];
  for (const run of runs) {
    if (!run.text) continue;
    const style = stripShellEquivalentStyle(run.style, shell);
    const last = normalized[normalized.length - 1];
    if (last && stableStyle(last.style) === stableStyle(style)) {
      last.text += run.text;
    } else {
      normalized.push({
        id: run.id || createTextId('run'),
        text: run.text,
        style,
      });
    }
  }
  return normalized;
}

function isListBlockType(type: TextBlockType): boolean {
  return type === 'ordered-list-item' || type === 'unordered-list-item';
}

function isBlockEmpty(block: TextBlock): boolean {
  return !textBlockToPlainText(block).trim();
}

/**
 * 提交时仅清理「整段为空」的列表文档，保留段落空行（回车产生的空块）。
 * 展示态对无内容的列表项不渲染 marker，见 TextDocumentRenderer。
 */
export function pruneEmptyListBlocks(
  blocks: TextBlock[],
  defaultBlockType: TextBlockType = 'paragraph',
): TextBlock[] {
  if (!blocks.length) {
    return [{ id: createTextId('block'), type: defaultBlockType, runs: [] }];
  }

  const allEmpty = blocks.every(isBlockEmpty);
  const allList = blocks.every((block) => isListBlockType(block.type));
  if (allEmpty && allList) {
    return [{ id: createTextId('block'), type: defaultBlockType, runs: [] }];
  }

  if (blocks.length === 1) {
    const only = blocks[0]!;
    if (isBlockEmpty(only) && isListBlockType(only.type)) {
      return [{ id: createTextId('block'), type: defaultBlockType, runs: [] }];
    }
  }

  return blocks;
}

export function normalizeTextDocument(
  document: TextDocument | undefined,
  options: {
    shellStyle?: TypographyStyle;
    defaultBlockType?: TextBlockType;
    finalize?: boolean;
  } = {},
): TextDocument {
  const defaultType = options.defaultBlockType ?? 'paragraph';
  let blocks = document?.blocks?.length
    ? document.blocks
    : [{ id: createTextId('block'), type: defaultType, runs: [] }];

  blocks = blocks.map<TextBlock>((block) => ({
    id: block.id || createTextId('block'),
    type: block.type,
    runs: normalizeRuns(block.runs ?? [], options.shellStyle),
  }));

  if (options.finalize) {
    blocks = pruneEmptyListBlocks(blocks, defaultType);
  }

  return {
    version: 1,
    blocks,
  };
}

export function normalizeElementText(
  text: TextContentData,
  options: { finalize?: boolean; shellStyle?: TypographyStyle } = {},
): TextContentData {
  const document = normalizeTextDocument(text.document, {
    shellStyle: options.shellStyle,
    finalize: options.finalize,
  });
  return {
    ...text,
    contentVersion: 1,
    document,
    content: textDocumentToPlainText(document),
  };
}

export function createTextDocumentFromPlainText(
  plainText: string,
  blockType: TextBlockType = 'paragraph',
  style?: InlineTextStyle,
): TextDocument {
  const lines = plainText.split('\n');
  return normalizeTextDocument({
    version: 1,
    blocks: (lines.length ? lines : ['']).map((line) => ({
      id: createTextId('block'),
      type: blockType,
      runs: line ? [{ id: createTextId('run'), text: line, style }] : [],
    })),
  });
}

export function scaleTextDocumentFontSizes(
  document: TextDocument,
  scale: number,
  baseFontSize: number,
): TextDocument {
  return normalizeTextDocument({
    version: 1,
    blocks: document.blocks.map((block) => ({
      ...block,
      runs: block.runs.map((run) => ({
        ...run,
        style: run.style?.fontSize
          ? { ...run.style, fontSize: Math.max(1, Math.round(run.style.fontSize * scale)) }
          : run.style,
      })),
    })),
  }, { shellStyle: { fontFamily: '', fontSize: baseFontSize, fontWeight: 400, color: '' } });
}
